import { useEffect, useState } from 'react';
import { HorizontalBarChart } from '../../components/dashboard/HorizontalBarChart';
import { StatCard } from '../../components/dashboard/StatCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { fetchSupportAnalytics, ServiceSupportAnalytics } from '../../services/supportAnalyticsApi';

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

function formatPercent(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

function formatMinutes(value: number): string {
  if (value < 60) return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} min`;
  const hours = Math.floor(value / 60);
  const minutes = Math.round(value % 60);
  return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
}

export function SupportAnalyticsPage() {
  const [analytics, setAnalytics] = useState<ServiceSupportAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(false);
      try {
        setAnalytics(await fetchSupportAnalytics());
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <LoadingSpinner label="Carregando analytics de suporte..." />;
  }

  if (error || !analytics) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
        Não foi possível carregar o analytics de suporte. Tente novamente em instantes.
      </div>
    );
  }

  const { summary } = analytics;

  return (
    <PageShell variant="dashboard">
      <PageHeader
        title="Analytics de suporte"
        description={`Volume, MTTR e conformidade de SLA — atualizado em ${new Date(analytics.generatedAtUtc).toLocaleString('pt-BR')}`}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total de chamados" value={formatNumber(summary.totalTickets)} />
        <StatCard label="Em aberto" value={formatNumber(summary.openTickets)} />
        <StatCard label="Incidentes" value={formatNumber(summary.incidentCount)} />
        <StatCard label="Solicitações" value={formatNumber(summary.requestCount)} />
        <StatCard label="Resolvidos" value={formatNumber(summary.resolvedCount)} />
        <StatCard label="MTTR" value={formatMinutes(summary.meanTimeToResolveMinutes)} />
        <StatCard
          label="Tempo médio 1ª resposta"
          value={formatMinutes(summary.meanTimeToFirstResponseMinutes)}
        />
        <StatCard
          label="SLA resposta"
          value={formatPercent(summary.slaResponseCompliancePercent)}
        />
        <StatCard
          label="SLA resolução"
          value={formatPercent(summary.slaResolutionCompliancePercent)}
        />
      </div>

      <section className="mt-6 grid gap-4 xl:grid-cols-2">
        <HorizontalBarChart
          title="Chamados por status"
          items={analytics.byStatus.map((item) => ({
            label: item.label,
            value: item.count,
            count: item.count,
          }))}
          valueFormatter={formatNumber}
        />

        <HorizontalBarChart
          title="Chamados por tipo"
          items={analytics.byType.map((item) => ({
            label: item.label === 'Incident' ? 'Incidente' : item.label === 'Request' ? 'Solicitação' : item.label,
            value: item.count,
            count: item.count,
          }))}
          valueFormatter={formatNumber}
        />

        <HorizontalBarChart
          title="Top itens de catálogo"
          items={analytics.byCatalogItem.map((item) => ({
            label: item.label,
            value: item.count,
            count: item.count,
          }))}
          valueFormatter={formatNumber}
        />
      </section>
    </PageShell>
  );
}
